/**
 * Admin -- Notifications/Channels (Wave 1b W1b-24)
 *
 * Wired to /api/v1/admin/notifications/channels. Mock data has been
 * removed; first paint pulls from the backend (7 default channels are
 * seeded for the F61 admin org by the b43 migration so the page is
 * never empty in dev).
 *
 * Test-channel button simulates 200ms round-trip + writes
 * last_test_at/outcome on the row. Real transports land with W1b-25
 * (next round). webhook channels with config.fakeFailure=true return
 * a simulated failure -- useful for E2E testing.
 *
 * ITAR-approved flip is a high-risk action. Backend emits an
 * audit_chain row tagged highRiskAction=true for both flip directions.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import {
  Plus, Pencil, Trash2, Loader2, Mail, MessageSquare, Webhook,
  Phone, Bell, Send, Shield, ShieldOff, AlertCircle, CheckCircle2,
  Clock, Moon, X,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { relativeTime } from '@/lib/format';
import { toast } from 'sonner';
import { SectionCard } from '@/components/admin';
import { TypedConfirmDialog } from '@/components/admin/typed-confirm-dialog';
import { apiClient } from '@/lib/api-client';

type ChannelType =
  | 'email'
  | 'slack'
  | 'pagerduty'
  | 'webhook'
  | 'sms'
  | 'in_app';

interface Channel {
  id: string;
  name: string;
  channelType: ChannelType;
  config: Record<string, unknown>;
  isEnabled: boolean;
  itarApproved: boolean;
  lastTestAt: string | null;
  // Issue #1114 — the v1 test endpoint is a stand-in that returns
  // `simulated` / `simulated_failure`. `success` / `failure` are
  // reserved for the future real-transport path (W1b-25).
  lastTestOutcome: 'success' | 'failure' | 'simulated' | 'simulated_failure' | null;
  lastTestError: string | null;
  createdAt: string;
  updatedAt: string;
}

const CHANNEL_TYPE_META: Record<
  ChannelType,
  { label: string; icon: React.ComponentType<{ className?: string }>; color: string }
> = {
  email: { label: 'Email', icon: Mail, color: 'text-blue-600 dark:text-blue-300' },
  slack: { label: 'Slack', icon: MessageSquare, color: 'text-purple-600 dark:text-purple-300' },
  pagerduty: { label: 'PagerDuty', icon: AlertCircle, color: 'text-emerald-600 dark:text-emerald-300' },
  webhook: { label: 'Webhook', icon: Webhook, color: 'text-amber-600 dark:text-amber-300' },
  sms: { label: 'SMS', icon: Phone, color: 'text-pink-600 dark:text-pink-300' },
  in_app: { label: 'In-app', icon: Bell, color: 'text-slate-600 dark:text-slate-300' },
};

export const Route = createFileRoute('/admin/notifications/channels')({
  component: ChannelsPage,
});

function ChannelsPage() {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Channel | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<Channel | null>(null);
  const [itarFlip, setItarFlip] = useState<Channel | null>(null);
  const [testing, setTesting] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.get<{ channels: Channel[] }>(
        '/admin/notifications/channels'
      );
      setChannels(data.channels);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load channels';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function deleteChannel(id: string) {
    try {
      await apiClient.delete(`/admin/notifications/channels/${id}`);
      toast.success('Channel deleted');
      setDeleting(null);
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      toast.error(msg);
    }
  }

  async function testChannel(id: string) {
    try {
      setTesting(id);
      // Issue #1114 — until the real transport worker (W1b-25) lands,
      // the backend returns `simulated` / `simulated_failure` and never
      // dispatches a real probe. Surface that clearly to the operator.
      const res = await apiClient.post<{
        outcome: 'success' | 'failure' | 'simulated' | 'simulated_failure';
        simulated?: boolean;
        message?: string;
        error: string | null;
      }>(`/admin/notifications/channels/${id}/test`, {});
      const successToastMsg =
        res.message ?? 'Test message succeeded';
      const failureToastMsg =
        res.message ??
        `Test failed: ${res.error ?? 'unknown error'}`;
      if (res.outcome === 'success') {
        toast.success(successToastMsg);
      } else if (res.outcome === 'simulated') {
        // Honest UX: a yellow info toast labelled "Simulated" — never
        // a green success toast — so the operator cannot mistake this
        // for a real delivery confirmation.
        toast.warning(`Simulated — ${successToastMsg}`);
      } else if (res.outcome === 'simulated_failure') {
        toast.warning(`Simulated failure — ${failureToastMsg}`);
      } else {
        toast.error(failureToastMsg);
      }
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Test failed';
      toast.error(msg);
    } finally {
      setTesting(null);
    }
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Notification Channels</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Delivery destinations for matched rules. ITAR-approved
            channels remain reachable when the tenant is in ITAR-only mode.
          </p>
        </div>
        <Button
          size="sm"
          className="h-8 gap-1.5"
          onClick={() => setCreating(true)}
          title="Create a new channel"
        >
          <Plus className="h-3.5 w-3.5" />
          Add channel
        </Button>
      </div>

      <SectionCard
        title="Channels"
        icon={Bell}
        description="Six channel types: email, Slack, PagerDuty, webhook, SMS, in-app. Test probes write last_test_at + outcome."
      >
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground p-4">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading channels…
          </div>
        ) : channels.length === 0 ? (
          <div className="text-xs text-muted-foreground p-4 italic">
            No channels yet. Click “Add channel” to create one.
          </div>
        ) : (
          <div className="border rounded-md divide-y">
            <div className="grid grid-cols-12 px-3 py-2 text-xs font-medium text-muted-foreground bg-muted/40">
              <div className="col-span-3">Name</div>
              <div className="col-span-2">Type</div>
              <div className="col-span-3">Last test</div>
              <div className="col-span-1 text-center">ITAR</div>
              <div className="col-span-1 text-center">Enabled</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {channels.map((c) => (
              <ChannelRow
                key={c.id}
                channel={c}
                onTest={() => testChannel(c.id)}
                onEdit={() => setEditing(c)}
                onDelete={() => setDeleting(c)}
                onItarFlip={() => setItarFlip(c)}
                testing={testing === c.id}
              />
            ))}
          </div>
        )}
      </SectionCard>

      {creating && (
        <ChannelEditorSheet
          mode="create"
          open
          onClose={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false);
            await refresh();
          }}
        />
      )}
      {editing && (
        <ChannelEditorSheet
          mode="edit"
          channel={editing}
          open
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      )}
      {deleting && (
        <TypedConfirmDialog
          open
          onOpenChange={(v) => !v && setDeleting(null)}
          title="Delete notification channel"
          description={`Permanently delete "${deleting.name}". Existing rules referencing this channel will retain the orphan id until edited.`}
          consequences={[
            `Channel "${deleting.name}" (${deleting.channelType}) will be permanently deleted`,
            'Rules referencing this channel will retain the orphan id until manually edited',
            'Delivery history rows will retain the orphan channel_id (FK SET NULL)',
            'Action is logged to the audit chain as a high-risk event',
          ]}
          confirmPhrase={deleting.name}
          confirmLabel="Delete channel"
          onConfirm={() => deleteChannel(deleting.id)}
        />
      )}
      {itarFlip && (
        <ItarFlipDialog
          channel={itarFlip}
          open
          onClose={() => setItarFlip(null)}
          onSaved={async () => {
            setItarFlip(null);
            await refresh();
          }}
        />
      )}

      {/* ─── Wave 1b W1b-28: Quiet Hours sub-section ────────────────── */}
      <QuietHoursSection />
    </div>
  );
}

function ChannelRow({
  channel, onTest, onEdit, onDelete, onItarFlip, testing,
}: {
  channel: Channel;
  onTest: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onItarFlip: () => void;
  testing: boolean;
}) {
  const meta = CHANNEL_TYPE_META[channel.channelType];
  const Icon = meta.icon;
  return (
    <div className="grid grid-cols-12 px-3 py-2.5 items-center text-sm">
      <div className="col-span-3">
        <div className="font-medium truncate" title={channel.name}>{channel.name}</div>
        <div className="text-[10px] text-muted-foreground">
          updated {relativeTime(channel.updatedAt)}
        </div>
      </div>
      <div className="col-span-2 flex items-center gap-1.5">
        <Icon className={cn('h-3.5 w-3.5', meta.color)} />
        <span className="text-xs">{meta.label}</span>
      </div>
      <div className="col-span-3 text-xs">
        {channel.lastTestAt ? (
          <div className="flex items-center gap-1.5">
            {/* Issue #1114 — render `simulated` / `simulated_failure`
                as an amber "Simulated" badge so the operator can never
                mistake the v1 stand-in for a real delivery probe. */}
            {channel.lastTestOutcome === 'success' ? (
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
            ) : channel.lastTestOutcome === 'simulated' ? (
              <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
            ) : channel.lastTestOutcome === 'simulated_failure' ? (
              <AlertCircle className="h-3.5 w-3.5 text-amber-600" />
            ) : (
              <AlertCircle className="h-3.5 w-3.5 text-red-500" />
            )}
            <span>
              {channel.lastTestOutcome === 'success'
                ? 'OK'
                : channel.lastTestOutcome === 'simulated'
                  ? 'Simulated'
                  : channel.lastTestOutcome === 'simulated_failure'
                    ? 'Simulated failure'
                    : 'Failed'}
              {' · '}
              <span className="text-muted-foreground">
                {relativeTime(channel.lastTestAt)}
              </span>
            </span>
          </div>
        ) : (
          <span className="text-muted-foreground italic">never tested</span>
        )}
        {channel.lastTestError && (
          <div className="text-[10px] text-red-500 truncate" title={channel.lastTestError}>
            {channel.lastTestError}
          </div>
        )}
      </div>
      <div className="col-span-1 flex justify-center">
        <Button
          size="icon"
          variant="ghost"
          className={cn(
            'h-7 w-7',
            channel.itarApproved
              ? 'text-emerald-600 dark:text-emerald-300'
              : 'text-muted-foreground'
          )}
          title={
            channel.itarApproved
              ? 'ITAR-approved (click to revoke)'
              : 'Not ITAR-approved (click to approve)'
          }
          onClick={onItarFlip}
        >
          {channel.itarApproved ? (
            <Shield className="h-3.5 w-3.5" />
          ) : (
            <ShieldOff className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>
      <div className="col-span-1 flex justify-center">
        <Badge
          variant="outline"
          className={cn(
            'text-[10px] py-0 h-4',
            channel.isEnabled
              ? 'border-emerald-500/40 text-emerald-700 dark:text-emerald-300'
              : 'border-slate-500/40 text-muted-foreground'
          )}
        >
          {channel.isEnabled ? 'on' : 'off'}
        </Badge>
      </div>
      <div className="col-span-2 flex justify-end gap-1">
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          title="Send a test message through this channel"
          onClick={onTest}
          disabled={testing || !channel.isEnabled}
        >
          {testing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          title="Edit channel"
          onClick={onEdit}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          title="Delete channel"
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

function ChannelEditorSheet({
  mode, channel, open, onClose, onSaved,
}: {
  mode: 'create' | 'edit';
  channel?: Channel;
  open: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(channel?.name ?? '');
  const [channelType, setChannelType] = useState<ChannelType>(
    (channel?.channelType as ChannelType) ?? 'email'
  );
  const [configText, setConfigText] = useState(
    JSON.stringify(channel?.config ?? defaultConfigFor('email'), null, 2)
  );
  const [isEnabled, setIsEnabled] = useState(channel?.isEnabled ?? true);
  const [itarApproved, setItarApproved] = useState(channel?.itarApproved ?? false);
  const [saving, setSaving] = useState(false);

  function handleTypeChange(t: ChannelType) {
    setChannelType(t);
    if (mode === 'create') {
      setConfigText(JSON.stringify(defaultConfigFor(t), null, 2));
    }
  }

  async function save() {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    let config: Record<string, unknown>;
    try {
      config = JSON.parse(configText);
    } catch {
      toast.error('Config must be valid JSON');
      return;
    }

    try {
      setSaving(true);
      if (mode === 'create') {
        await apiClient.post('/admin/notifications/channels', {
          name: name.trim(),
          channelType,
          config,
          isEnabled,
          itarApproved,
        });
        toast.success('Channel created');
      } else if (channel) {
        await apiClient.put(`/admin/notifications/channels/${channel.id}`, {
          name: name.trim(),
          config,
          isEnabled,
          itarApproved,
        });
        toast.success('Channel updated');
      }
      await onSaved();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-[540px] sm:max-w-[540px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{mode === 'create' ? 'Add channel' : 'Edit channel'}</SheetTitle>
          <SheetDescription>
            Channel type cannot be changed after creation. Config shape is
            validated against the channel type at submit time.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-5">
          <div className="space-y-1.5">
            <Label htmlFor="ch-name">Name</Label>
            <Input
              id="ch-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Admin Email"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Channel type</Label>
            <Select
              value={channelType}
              onValueChange={(v) => handleTypeChange(v as ChannelType)}
              disabled={mode === 'edit'}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(CHANNEL_TYPE_META) as ChannelType[]).map((t) => (
                  <SelectItem key={t} value={t}>
                    {CHANNEL_TYPE_META[t].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">
              {mode === 'edit'
                ? 'Channel type is immutable -- delete + recreate to change.'
                : 'Choosing a type populates a default config below.'}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ch-config">Config (JSON)</Label>
            <Textarea
              id="ch-config"
              rows={8}
              value={configText}
              onChange={(e) => setConfigText(e.target.value)}
              className="font-mono text-xs"
            />
            <p className="text-[11px] text-muted-foreground">
              {configHelpFor(channelType)}
            </p>
          </div>

          <Separator />

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="flex items-center gap-2">Enabled</Label>
              <Switch
                checked={isEnabled}
                onCheckedChange={setIsEnabled}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-2">
                <Shield className="h-3.5 w-3.5" />
                ITAR approved
              </Label>
              <Switch
                checked={itarApproved}
                onCheckedChange={setItarApproved}
              />
            </div>
          </div>

          <p className="text-[11px] text-muted-foreground">
            Toggling ITAR approval here only persists the flag. The
            high-risk audit row + cascade preview live on the dedicated
            ITAR-approval action (icon column on the channel row).
          </p>

          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              {mode === 'create' ? 'Create channel' : 'Save changes'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ItarFlipDialog({
  channel, open, onClose, onSaved,
}: {
  channel: Channel;
  open: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [justification, setJustification] = useState('');
  const [saving, setSaving] = useState(false);

  const newApproved = !channel.itarApproved;

  async function save() {
    if (justification.trim().length < 10) {
      toast.error('Justification must be at least 10 characters');
      return;
    }
    try {
      setSaving(true);
      await apiClient.patch(
        `/admin/notifications/channels/${channel.id}/itar-approved`,
        { approved: newApproved, justification: justification.trim() }
      );
      toast.success(newApproved ? 'Channel ITAR-approved' : 'ITAR approval revoked');
      await onSaved();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {newApproved ? 'Approve channel for ITAR mode' : 'Revoke ITAR approval'}
          </DialogTitle>
          <DialogDescription>
            High-risk action. An audit chain entry will be written with your
            justification. The channel "{channel.name}" will{' '}
            {newApproved ? 'become' : 'no longer be'} reachable while the
            tenant is in ITAR-only mode.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Label htmlFor="itar-just">Justification (min 10 characters)</Label>
          <Textarea
            id="itar-just"
            rows={4}
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            placeholder="Explain why this approval is changing"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            {newApproved ? 'Approve' : 'Revoke'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function defaultConfigFor(t: ChannelType): Record<string, unknown> {
  switch (t) {
    case 'email':
      return { to: ['admin@example.com'], subjectPrefix: '[ArgusAI]' };
    case 'slack':
      return { webhookUrl: 'https://hooks.slack.com/services/T000/B000/XXXX', channel: '#alerts' };
    case 'pagerduty':
      return { integrationKey: 'PD_INTEGRATION_KEY', severity: 'critical' };
    case 'webhook':
      return { url: 'https://siem.example.com/webhook', method: 'POST' };
    case 'sms':
      return { to: ['+15555550100'], provider: 'twilio' };
    case 'in_app':
      return { roles: ['admin', 'owner'] };
  }
}

function configHelpFor(t: ChannelType): string {
  switch (t) {
    case 'email':
      return 'Required: { "to": ["addr@..."] }. Optional: subjectPrefix.';
    case 'slack':
      return 'Required: { "webhookUrl": "..." }. Optional: channel.';
    case 'pagerduty':
      return 'Required: { "integrationKey": "..." }. Optional: severity.';
    case 'webhook':
      return 'Required: { "url": "..." }. Optional: method, signingSecret. Test escape hatch: fakeFailure=true.';
    case 'sms':
      return 'Required: { "to": ["+1..."] }. Optional: provider.';
    case 'in_app':
      return 'Optional: { "roles": [...] } and/or { "userIds": [...] }.';
  }
}

// ════════════════════════════════════════════════════════════════════════════
// Wave 1b W1b-28 -- Quiet Hours sub-section
// ════════════════════════════════════════════════════════════════════════════
//
// Per-user quiet-hours configuration. Each authenticated caller can set
// ONE active config (timezone + window + days + severity floor). Admins
// can set/inspect any user's config. The W1b-25 dispatcher consults this
// before fan-out via isQuietHoursActive() and writes
// status='suppressed', error_message='quiet-hours' for blocked deliveries.

const TOP_30_TIMEZONES: string[] = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Phoenix',
  'America/Toronto',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Stockholm',
  'Europe/Moscow',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Sydney',
  'Pacific/Auckland',
];

const DAY_LABELS: { value: number; label: string; short: string }[] = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
];

type SeverityFloor = 'info' | 'warning' | 'critical';

interface QuietHoursConfig {
  id: string;
  userId: string;
  timezone: string;
  startTime: string;
  endTime: string;
  daysOfWeek: number[];
  severityFloor: SeverityFloor;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

function trimSeconds(t: string): string {
  // Postgres `time` round-trips as 'HH:MM:SS'. Inputs from <input type=time>
  // are 'HH:MM'. Display the latter consistently in the form.
  const m = /^(\d{2}:\d{2})/.exec(t);
  return m ? m[1] : t;
}

function QuietHoursSection() {
  const [me, setMe] = useState<QuietHoursConfig | null>(null);
  const [meLoading, setMeLoading] = useState(true);
  const [adminConfigs, setAdminConfigs] = useState<QuietHoursConfig[]>([]);
  const [adminLoading, setAdminLoading] = useState(true);
  const [editing, setEditing] = useState<{
    target: 'me' | string;
    initial?: QuietHoursConfig;
  } | null>(null);

  const refresh = useCallback(async () => {
    setMeLoading(true);
    setAdminLoading(true);
    try {
      // /me may 404 if caller has no config; treat that as "not set".
      const meRes = await apiClient
        .get<QuietHoursConfig>('/admin/notifications/quiet-hours/me')
        .catch((err: unknown) => {
          // 404 -> null (no config set yet).
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const status = (err as any)?.status;
          if (status === 404) return null;
          throw err;
        });
      setMe(meRes);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load quiet hours';
      toast.error(msg);
      setMe(null);
    } finally {
      setMeLoading(false);
    }

    try {
      const data = await apiClient.get<{ configs: QuietHoursConfig[] }>(
        '/admin/notifications/quiet-hours?isActive=true&limit=200'
      );
      setAdminConfigs(data.configs);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load configs';
      toast.error(msg);
      setAdminConfigs([]);
    } finally {
      setAdminLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function clearMyQuietHours() {
    try {
      await apiClient.delete('/admin/notifications/quiet-hours/me');
      toast.success('Quiet hours cleared');
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Clear failed';
      toast.error(msg);
    }
  }

  return (
    <>
      <SectionCard
        title="Quiet Hours (you)"
        icon={Moon}
        description="During this window non-critical notifications are suppressed for your account. Severity-floor overrides ensure critical alerts always reach you."
      >
        {meLoading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground p-4">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading your quiet hours…
          </div>
        ) : !me ? (
          <div className="p-4 text-sm flex items-center justify-between">
            <div className="text-muted-foreground">
              Not set. Click <span className="font-medium">Configure</span> to
              suppress non-critical notifications during specific hours.
            </div>
            <Button
              size="sm"
              onClick={() => setEditing({ target: 'me' })}
              title="Configure your quiet hours"
            >
              <Clock className="h-3.5 w-3.5 mr-1.5" /> Configure
            </Button>
          </div>
        ) : (
          <div className="p-4 grid grid-cols-12 gap-3 items-center">
            <div className="col-span-3">
              <div className="text-xs text-muted-foreground">Window</div>
              <div className="font-medium font-mono">
                {trimSeconds(me.startTime)} → {trimSeconds(me.endTime)}
              </div>
            </div>
            <div className="col-span-3">
              <div className="text-xs text-muted-foreground">Timezone</div>
              <div className="font-medium truncate" title={me.timezone}>
                {me.timezone}
              </div>
            </div>
            <div className="col-span-3">
              <div className="text-xs text-muted-foreground">Days</div>
              <div className="text-sm">
                {(me.daysOfWeek ?? []).length === 7
                  ? 'Every day'
                  : (me.daysOfWeek ?? [])
                      .map((d) => DAY_LABELS[d]?.short ?? d)
                      .join(', ')}
              </div>
            </div>
            <div className="col-span-1">
              <div className="text-xs text-muted-foreground">Floor</div>
              <Badge variant="secondary" className="capitalize">
                {me.severityFloor}
              </Badge>
            </div>
            <div className="col-span-2 text-right space-x-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing({ target: 'me', initial: me })}
                title="Edit quiet hours"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={clearMyQuietHours}
                title="Disable quiet hours"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="Quiet Hours (org-wide)"
        icon={Bell}
        description="All active quiet-hours configurations in your tenant. Admins can edit any row; useful for offboarding or coverage gaps."
      >
        {adminLoading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground p-4">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading configs…
          </div>
        ) : adminConfigs.length === 0 ? (
          <div className="text-xs text-muted-foreground p-4 italic">
            No active quiet-hours configurations in this org yet.
          </div>
        ) : (
          <div className="border rounded-md divide-y">
            <div className="grid grid-cols-12 px-3 py-2 text-xs font-medium text-muted-foreground bg-muted/40">
              <div className="col-span-3">User</div>
              <div className="col-span-2">Window</div>
              <div className="col-span-3">Timezone</div>
              <div className="col-span-2">Days</div>
              <div className="col-span-1 text-center">Floor</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>
            {adminConfigs.map((c) => (
              <div
                key={c.id}
                className="grid grid-cols-12 px-3 py-2 items-center text-sm"
              >
                <div className="col-span-3 font-mono text-xs truncate" title={c.userId}>
                  {c.userId.slice(0, 8)}…
                </div>
                <div className="col-span-2 font-mono">
                  {trimSeconds(c.startTime)}-{trimSeconds(c.endTime)}
                </div>
                <div className="col-span-3 truncate" title={c.timezone}>
                  {c.timezone}
                </div>
                <div className="col-span-2 text-xs">
                  {(c.daysOfWeek ?? []).length === 7
                    ? 'Every day'
                    : (c.daysOfWeek ?? [])
                        .map((d) => DAY_LABELS[d]?.short ?? d)
                        .join(', ')}
                </div>
                <div className="col-span-1 text-center">
                  <Badge variant="secondary" className="capitalize text-[10px]">
                    {c.severityFloor}
                  </Badge>
                </div>
                <div className="col-span-1 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2"
                    onClick={() =>
                      setEditing({ target: c.userId, initial: c })
                    }
                    title="Edit as admin"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {editing && (
        <QuietHoursEditorSheet
          target={editing.target}
          initial={editing.initial ?? null}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      )}
    </>
  );
}

function QuietHoursEditorSheet({
  target,
  initial,
  onClose,
  onSaved,
}: {
  target: 'me' | string;
  initial: QuietHoursConfig | null;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [timezone, setTimezone] = useState(initial?.timezone ?? 'UTC');
  const [startTime, setStartTime] = useState(
    initial ? trimSeconds(initial.startTime) : '22:00'
  );
  const [endTime, setEndTime] = useState(
    initial ? trimSeconds(initial.endTime) : '07:00'
  );
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>(
    initial?.daysOfWeek ?? [0, 1, 2, 3, 4, 5, 6]
  );
  const [severityFloor, setSeverityFloor] = useState<SeverityFloor>(
    initial?.severityFloor ?? 'critical'
  );
  const [saving, setSaving] = useState(false);

  function toggleDay(d: number) {
    setDaysOfWeek((prev) =>
      prev.includes(d)
        ? prev.filter((x) => x !== d)
        : [...prev, d].sort((a, b) => a - b)
    );
  }

  async function save() {
    if (daysOfWeek.length === 0) {
      toast.error('Pick at least one day of week.');
      return;
    }
    setSaving(true);
    try {
      const url =
        target === 'me'
          ? '/admin/notifications/quiet-hours/me'
          : `/admin/notifications/quiet-hours/${target}`;
      await apiClient.put(url, {
        timezone,
        startTime,
        endTime,
        daysOfWeek,
        severityFloor,
      });
      toast.success(
        target === 'me' ? 'Quiet hours saved' : 'Quiet hours saved for user'
      );
      await onSaved();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle>
            {target === 'me'
              ? 'Quiet hours (your account)'
              : 'Quiet hours (admin override)'}
          </SheetTitle>
          <SheetDescription>
            Suppress non-critical notifications during this window. Severity
            floor overrides quiet hours -- events at or above the floor still
            deliver immediately.
          </SheetDescription>
        </SheetHeader>

        <div className="py-5 space-y-4">
          <div className="space-y-1">
            <Label htmlFor="qh-tz">Timezone</Label>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger id="qh-tz" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TOP_30_TIMEZONES.map((tz) => (
                  <SelectItem key={tz} value={tz}>
                    {tz}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="qh-start">Start time</Label>
              <Input
                id="qh-start"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="qh-end">End time</Label>
              <Input
                id="qh-end"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="h-9"
              />
            </div>
          </div>
          <div className="text-[11px] text-muted-foreground -mt-2">
            Overnight wrap is fine -- e.g. 22:00 → 07:00 means "in window from
            10pm until 7am the next day".
          </div>

          <div className="space-y-1">
            <Label>Days of week</Label>
            <div className="flex flex-wrap gap-1.5">
              {DAY_LABELS.map((d) => {
                const active = daysOfWeek.includes(d.value);
                return (
                  <Button
                    key={d.value}
                    size="sm"
                    variant={active ? 'default' : 'outline'}
                    className="h-7 px-2 text-xs"
                    onClick={() => toggleDay(d.value)}
                    title={d.label}
                  >
                    {d.short}
                  </Button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="qh-floor">
              Severity floor (events at or above always deliver)
            </Label>
            <Select
              value={severityFloor}
              onValueChange={(v) => setSeverityFloor(v as SeverityFloor)}
            >
              <SelectTrigger id="qh-floor" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="info">
                  info -- suppress everything (most aggressive)
                </SelectItem>
                <SelectItem value="warning">
                  warning -- still deliver warning + critical
                </SelectItem>
                <SelectItem value="critical">
                  critical -- still deliver critical (recommended)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Separator />
        <div className="pt-4 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={save} disabled={saving}>
            {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            Save
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
